import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

function parseYm(value: string): { year: number; month: number } {
  const [y, m] = value.split("-").map((s) => parseInt(s, 10))
  return { year: y, month: m }
}

function toYm(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`
}

function thisMonth(): { year: number; month: number } {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

function lastCompleteMonth(): string {
  const { year, month } = thisMonth()
  if (month === 1) return toYm(year - 1, 12)
  return toYm(year, month - 1)
}

function isAfter(a: { year: number; month: number }, b: { year: number; month: number }): boolean {
  if (a.year !== b.year) return a.year > b.year
  return a.month > b.month
}

function stepMonth(value: string, delta: number): string {
  const { year, month } = parseYm(value)
  const total = year * 12 + (month - 1) + delta
  return toYm(Math.floor(total / 12), (total % 12) + 1)
}

export interface MonthYearPickerProps {
  /** Value in "YYYY-MM" format */
  value: string
  onChange: (value: string) => void
  /** Months (in "YYYY-MM" format) that contain data — rendered emphasised */
  monthsWithData?: string[]
  /** Latest allowed month. Defaults to current month. Pass null to disable upper bound. */
  maxMonth?: string | null
  /** Earliest allowed month. */
  minMonth?: string
  className?: string
  disabled?: boolean
}

export function MonthYearPicker({
  value,
  onChange,
  monthsWithData,
  maxMonth,
  minMonth,
  className,
  disabled,
}: MonthYearPickerProps) {
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState<number>(() => parseYm(value).year)

  useEffect(() => {
    if (open) setViewYear(parseYm(value).year)
  }, [open, value])

  const selected = parseYm(value)
  const max = maxMonth === null ? null : parseYm(maxMonth ?? toYm(thisMonth().year, thisMonth().month))
  const min = minMonth ? parseYm(minMonth) : null

  const monthsWithDataSet = useMemo(
    () => new Set(monthsWithData ?? []),
    [monthsWithData],
  )

  const label = `${MONTHS_LONG[selected.month - 1]} ${selected.year}`

  const canGoPrev = !min || isAfter(selected, min)
  const canGoNext = !max || isAfter(max, selected)

  const gridRef = useRef<HTMLDivElement>(null)
  const focusMonth = (newMonth: number, newYear: number) => {
    if (newYear !== viewYear) setViewYear(newYear)
    requestAnimationFrame(() => {
      const btn = gridRef.current?.querySelector<HTMLButtonElement>(
        `[data-month="${newMonth}"][data-year="${newYear}"]`,
      )
      btn?.focus()
    })
  }

  const handleGridKey = (e: React.KeyboardEvent, m: number) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault()
      if (m === 1) focusMonth(12, viewYear - 1)
      else focusMonth(m - 1, viewYear)
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      if (m === 12) focusMonth(1, viewYear + 1)
      else focusMonth(m + 1, viewYear)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      if (m <= 3) focusMonth(m + 9, viewYear - 1)
      else focusMonth(m - 3, viewYear)
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      if (m >= 10) focusMonth(m - 9, viewYear + 1)
      else focusMonth(m + 3, viewYear)
    } else if (e.key === "PageUp") {
      e.preventDefault()
      setViewYear((y) => y - 1)
    } else if (e.key === "PageDown") {
      e.preventDefault()
      setViewYear((y) => y + 1)
    }
  }

  const isDisabledCell = (y: number, m: number) => {
    if (min && isAfter(min, { year: y, month: m })) return true
    if (max && isAfter({ year: y, month: m }, max)) return true
    return false
  }

  return (
    <div className={cn("inline-flex items-center gap-1", className)}>
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={() => onChange(stepMonth(value, -1))}
        disabled={disabled || !canGoPrev}
        aria-label="Previous month"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className="h-9 w-[180px] justify-center font-medium tabular-nums"
          >
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-3" align="center">
          <div className="flex items-center justify-between pb-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setViewYear((y) => y - 1)}
              aria-label="Previous year"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-semibold tabular-nums">{viewYear}</div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setViewYear((y) => y + 1)}
              aria-label="Next year"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div ref={gridRef} className="grid grid-cols-3 gap-1">
            {MONTHS_SHORT.map((name, i) => {
              const m = i + 1
              const isSelected = selected.year === viewYear && selected.month === m
              const ym = toYm(viewYear, m)
              const hasData = monthsWithDataSet.has(ym)
              const disabledCell = isDisabledCell(viewYear, m)
              return (
                <button
                  key={m}
                  type="button"
                  data-month={m}
                  data-year={viewYear}
                  disabled={disabledCell}
                  onClick={() => {
                    onChange(ym)
                    setOpen(false)
                  }}
                  onKeyDown={(e) => handleGridKey(e, m)}
                  className={cn(
                    "inline-flex h-9 items-center justify-center rounded-md text-sm font-medium transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                    "disabled:pointer-events-none disabled:opacity-30",
                    isSelected
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : hasData
                      ? "bg-accent/60 text-accent-foreground hover:bg-accent"
                      : "hover:bg-accent hover:text-accent-foreground text-muted-foreground",
                  )}
                >
                  {name}
                </button>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={() => onChange(stepMonth(value, 1))}
        disabled={disabled || !canGoNext}
        aria-label="Next month"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

export { lastCompleteMonth, stepMonth, toYm, parseYm }
